import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faCircleX } from "@fortawesome/pro-regular-svg-icons/faCircleX";
import { faCommentEdit } from "@fortawesome/pro-regular-svg-icons/faCommentEdit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Button, Card, CardActions, CardContent, Grid, Pagination, Stack } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT, FEED_CARD_WIDTH } from "app/appConstants";
import { AppImages } from "app/appImages";
import { updateFoundationCallouts } from "app/features/callout/calloutSlice";
import { store } from "app/store";
import { MprcBarTitle, MprcContainer, MprcFormBarHeader, MprcIconButtonDropdown, MprcInfiniteLoading, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonBack } from "components/buttons";
import { MprcFilterBox, MprcFilterCount, MprcFilterLine } from "components/filter";
import { IconSort } from "components/icons";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { MprcSearchTextField } from "components/MprcSearchTextField";
import { ManagedAutoRegisterOrder, ManagedAutoRegisterPendingApprovalDto, ManagedAutoRegisterPendingApprovalDtoPaginatedList } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ReplyDialogResult, ReplyDrawer, ReplyStatus } from "views/common/Approvals/ReplyDrawer";

interface IFilter {
    searchText: string;
    order: ManagedAutoRegisterOrder;
}

interface BuildCardProps {
    item: ManagedAutoRegisterPendingApprovalDto;
    onComplete: () => void;
}

export const ManageAutoRegisterApprovals = () => {

    const api = useCoreApi();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [filtered, setFiltered] = useState<boolean>(false);
    const [viewData, setViewData] = useState<ManagedAutoRegisterPendingApprovalDtoPaginatedList>();
    const [filterOptions, setFilterOptions] = useState<IFilter>({ searchText: "", order: ManagedAutoRegisterOrder.Oldest });

    const pageNumber = useRef<number>(1);

    ///
    const handleReset = () => {
        if (filterOptions.searchText != "") {
            setFilterOptions({ ...filterOptions, searchText: "" });
        }
    };


    ///
    const handleChangeFilter = (filter: Partial<IFilter>) => {
        setFilterOptions({ ...filterOptions, ...filter });
        setLoading(true);
    };

    ///
    const removeCard = (item: ManagedAutoRegisterPendingApprovalDto) => {
        if (viewData!.items.length == 1) {
            if (pageNumber.current > 1) {
                pageNumber.current--;
            } else {
                // Limpar callout pois não há mais items
                store.dispatch(updateFoundationCallouts([{ id: "UserAutoRegisterPendings", active: false }]));
            }

            setLoading(true);
        }
        else {
            const newState: ManagedAutoRegisterPendingApprovalDtoPaginatedList = {
                ...viewData!,
                items: viewData!.items.filter(p => p.userId != item.userId)
            };
            setViewData(newState);
        }
    };

    async function loadData() {
        try {
            var result = await api.v1.userManagement.autoRegisterPendingApprovals({
                searchText: filterOptions.searchText,
                order: filterOptions.order,
                pageNumber: pageNumber.current,
                pageSize: APP_PAGE_SIZE_DEFAULT
            });

            setViewData(result.data);
            setFiltered(filterOptions.searchText != "");
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    useEffect(() => {
        if (loading) loadData();
    }, [loading]);

    return <>
        <MprcContainer>
            <MprcContainer>
                <MprcFormBarHeader actions={
                    [
                        <ButtonBack rounded size="large" variant="text" onClick={() => navigate(-1)} />,
                        <MprcBarTitle key="barTitle">{t("User.AutoRegisterApprovals.Title")}</MprcBarTitle>
                    ]
                } />
            </MprcContainer>
            <MprcFilterBox>
                <MprcFilterLine>
                    <MprcSearchTextField
                        placeholder={t("User.AutoRegisterApprovals.SearchTextPlaceholder")}
                        initialValue={filterOptions.searchText}
                        autoFocus
                        confirmMode="onEnter"
                        onConfirm={(value) => handleChangeFilter({ searchText: value })}
                    />

                    <MprcIconButtonDropdown
                        menuItemCheck
                        icon={<IconSort />}
                        title={t("shared:Filter.OrderBy")}
                        selected={filterOptions.order}
                        onChange={(order) => handleChangeFilter({ order })}
                        items={
                            [
                                { text: t("User.AutoRegisterApprovals.OrderBy.Oldest"), value: ManagedAutoRegisterOrder.Oldest },
                                { text: t("User.AutoRegisterApprovals.OrderBy.Newest"), value: ManagedAutoRegisterOrder.Newest }
                            ]
                        }
                    />
                </MprcFilterLine>
            </MprcFilterBox>
        </MprcContainer>

        {viewError != null
            ? <MprcRequestErrorReport error={viewError} />
            : (
                <>
                    <MprcInfiniteLoading visible={loading} />
                    {!loading &&
                        <>
                            <MprcContainer>
                                <MprcFilterCount
                                    loading={loading}
                                    isEmpty={viewData?.totalRows == 0}
                                    countText={t("User.AutoRegisterApprovals.RecordsFoundText", { count: viewData?.totalRows ?? 0 })}
                                    filter={
                                        {
                                            active: filtered,
                                            onResetFilter: handleReset,
                                            text: t("User.AutoRegisterApprovals.NoRecordsFoundText")
                                        }
                                    }
                                />
                            </MprcContainer>

                            {
                                viewData != undefined &&
                                <Box width={FEED_CARD_WIDTH} sx={{ margin: "0 auto" }}>
                                    {
                                        viewData.items.map((item, index) =>
                                            <BuildCard
                                                key={index}
                                                item={item}
                                                onComplete={() => removeCard(item)}
                                            />
                                        )
                                    }

                                    {
                                        viewData.totalPages > 1 &&
                                        <Stack alignItems="center">
                                            <Pagination
                                                shape="rounded"
                                                color="primary"
                                                page={viewData.pageNumber}
                                                count={viewData.totalPages}
                                                onChange={(e, number) => {
                                                    if (number != pageNumber.current) {
                                                        pageNumber.current = number;
                                                        setLoading(true);
                                                    }
                                                }}
                                            />
                                        </Stack>
                                    }
                                </Box>
                            }
                        </>
                    }
                </>
            )
        }
    </>;
};

const BuildCard = ({ item, onComplete }: BuildCardProps) => {

    const api = useCoreApi();
    const { t } = useTranslation();
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const makeDecision = async (approved: boolean, message: string | null = null): Promise<boolean> => {
        setSaving(true);
        var result = false;

        try {
            const response = await api.v1.userManagement.moderateAutoRegister({ approved, userId: item.userId, message });
            if (response.data == true) {
                if (approved) enqueueSnackbar(t("User.AutoRegisterApprovals.ApproveMessage"), { variant: "success" });
                else enqueueSnackbar(t("User.AutoRegisterApprovals.RejectedMessage"), { variant: "success" });

                onComplete();
                result = true;
            }
            else {
                enqueueSnackbar(t("User.AutoRegisterApprovals.ErrorMessage"), { variant: "success" });
            }
        }
        catch (e) {
            MprcRequestSnackbarErrorReport(e);
        }
        finally {
            setSaving(false);
        }

        return result;
    };

    const handleClose = async (dialogResult: ReplyDialogResult | undefined) => {

        if (dialogResult) {
            const success = await makeDecision(dialogResult.status == ReplyStatus.Approved, dialogResult.message);
            if (success) setShowDialog(false);
        }
        else {
            setShowDialog(false);
        }

    };

    return (
        <MprcContainer>
            <ReplyDrawer
                open={showDialog}
                title={item.name}
                approveButtonText={t("User.AutoRegisterApprovals.ReplyApproveButtonText")}
                rejectButtonText={t("User.AutoRegisterApprovals.ReplyRejectButtonText")}
                onClose={handleClose}
            />

            <MprcRequestErrorDialog error={error} onClose={() => setError(null)} />

            <MprcRequestIndicator type="post" visible={saving} />

            <Card>
                <CardContent sx={{ p: 1, pb: 0 }}>

                    <Grid container gap={1} justifyContent="center">
                        <Grid size="grow">
                            <Grid container direction="column" gap={1}>
                                <Grid id="authorSection" width="100%">

                                    <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                        <Grid>
                                            <Avatar src={item.thumbnailUrl ?? AppImages.UserThumbnail} title={item.name} />
                                        </Grid>
                                        <Grid size="grow">
                                            <MprcFeedTypography variant="authorPrimary" component="div">
                                                <span title={item.name}>{item.name}</span>
                                            </MprcFeedTypography>

                                            <MprcFeedTypography variant="authorSecondary" component="div">
                                                {item.email}
                                            </MprcFeedTypography>
                                        </Grid>
                                    </Grid>

                                </Grid>
                                <Grid id="contentSection" width="100%">
                                    <MprcFeedTypography variant="content" component="div">
                                        {item.message}
                                    </MprcFeedTypography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                </CardContent>
                <CardActions>
                    {
                        <Grid id="footerSection" width="100%">

                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid>
                                    <Button variant="text" color="success" onClick={() => makeDecision(true, null)}>
                                        {<FontAwesomeIcon icon={faCircleCheck} fixedWidth />}
                                        <span>{t("User.AutoRegisterApprovals.ApproveButtonText")}</span>
                                    </Button>
                                </Grid>
                                <Grid>
                                    <Button variant="text" color="error" onClick={() => makeDecision(false, null)}>
                                        {<FontAwesomeIcon icon={faCircleX} fixedWidth />}
                                        <span>{t("User.AutoRegisterApprovals.RejectButtonText")}</span>
                                    </Button>
                                </Grid>
                                <Grid>
                                    <Button variant="text" color="muted" onClick={() => setShowDialog(true)}>
                                        {<FontAwesomeIcon icon={faCommentEdit} fixedWidth />}
                                        <span>{t("User.AutoRegisterApprovals.ReplyButtonText")}</span>
                                    </Button>
                                </Grid>
                            </Grid>

                        </Grid>
                    }
                </CardActions>
            </Card>

        </MprcContainer>
    );
};
